import { call, put, select, take, takeEvery } from "redux-saga/effects";

import { createHideLoadingIndicatorAction, createLoadFavoriteFormsAction, createSetFormAsFavoriteRequestAction, createShowLoadingIndicatorAction, FAVORITE_FORMS_LOADED } from "@multiweb/reactCore/bridge";
import { IFormProfile } from "@multiweb/types/contracts/menu";
import { SagaWithReturnType } from "@multiweb/types/react";
import { logger } from "../logger";
import { getOpenedFormInfo } from "../openedItems";
import { getFormItemCode, getFormProfile } from "../selectors";
import { IMenuItemDefaultActionAllowedAction, MenuItemActivatedType } from "../types";
import { createAllowedDefaultActionPattern } from "../utils";
import { isFormFavorite } from "./selectors";

export const saga: SagaWithReturnType = function* favoriteItemsSaga() {
    const { formFavoriteActionItem, openedInstanceFavoriteActionItem } = MenuItemActivatedType;
    yield takeEvery(createAllowedDefaultActionPattern(formFavoriteActionItem), formFavoriteActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openedInstanceFavoriteActionItem), openedInstanceFavoriteActionItemActivatedSaga);
};

/**
 * Obsluhuje načítání oblíbených formulářů. Zajišťuje zaslání požadavku a zobrazení loading indikátoru.
 * Data si zpracuje přímo reducer, proto není třeba tu s nimi nijak manipulovat.
 */
export const loadFavoriteFormsSaga: SagaWithReturnType = function* loadFavoriteFormsSaga() {
    yield put(createShowLoadingIndicatorAction());
    yield put(createLoadFavoriteFormsAction());
    yield take(FAVORITE_FORMS_LOADED);
    yield put(createHideLoadingIndicatorAction());
};

/**
 * Obsluhuje výchozí chování pro akční položku nastavení oblíbeného formuláře.
 * @param action
 */
export const formFavoriteActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* formFavoriteActionItemActivatedSaga(action) {
    const formItemId = action.payload.itemId as number; // položky formulářů mají číselné id, jedná se o formItemId, sága je spuštěná jen pro tento typ položek
    const profileId = action.payload.profileId;
    const formCode: ReturnType<typeof getFormItemCode> = yield select(getFormItemCode, formItemId);
    yield call(toggleFavoriteFormSaga, formItemId, formCode, profileId);
};

/**
 * Obsluhuje výchozí chování pro akční položku nastavení oblíbeného formuláře v otevřených položkách menu.
 * @param action
 */
export const openedInstanceFavoriteActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openedInstanceFavoriteActionItemActivatedSaga(action) {
    const formInstanceId = action.payload.itemId as string; // otevřené formuláře mají id typu string, jedná se o formInstanceId, sága je spuštěná jen pro tento typ položek
    const openedForm: ReturnType<typeof getOpenedFormInfo> = yield select(getOpenedFormInfo, formInstanceId);
    if (!openedForm) {
        yield call(logger.warn, "Unable to set form as favorite because form with instance id %s cannot be found", formInstanceId);
        return;
    }
    yield call(toggleFavoriteFormSaga, openedForm?.menuItemId, openedForm?.formCode, openedForm?.profileId);
};

/**
 * Obsluhuje nastavení oblíbeného formuláře. Změní stav oblíbenosti - pokud byl formulář oblíbený, již nebude a naopak.
 * @param menuId ID položky menu.
 * @param formCode Kód formuláře.
 */
export const toggleFavoriteFormSaga: SagaWithReturnType<[number | undefined, string | undefined, number | undefined]> = function* setFavoriteFormSaga(formId, formCode, profileId) {
    if (!formId || !formCode) {
        yield call(logger.warn, "Unable to toggle favorite form because menu id or form code is undefined");
        return;
    }
    const isFavorite: ReturnType<typeof isFormFavorite> = yield select(isFormFavorite, { formId, profileId });
    const profile = (profileId) ? yield select(getFormProfile, profileId) : undefined;
    yield put(createSetFormAsFavoriteRequestAction(formId as number, formCode as string, !isFavorite, profile as IFormProfile | undefined));
};
